import { buildSplitRecipientDetail } from "../../constants/recipientIdentity";
import { countryToIso } from "../../sanitize/foreignAddress";
import { streetLinesWithUnit } from "../1098/streetLines";
import { Record1099Q } from "./interface/1099-Q-interface";

/**
 * Form 1099-Q (Payments From Qualified Education Programs) detail, shared by the original and
 * correction generators. Element order follows Form1099QDetailType in
 * schemas/TY2026/FORM_TYPES/IRS-Form1099QType.xsd, which is identical to the TY2025 file.
 *
 * Written for the e-file app; there is no core-api generator to port. The recipient is built
 * through the shared split-name builder, the same way the 2026 NEC and 1098 records are.
 */

const text = (v: unknown): string => String(v ?? "").trim();

const isChecked = (v: unknown): boolean =>
  v === true || ["X", "1", "TRUE", "Y", "YES"].includes(text(v).toUpperCase());

/** "0" or "1" for a DigitBooleanType element. */
const digit = (v: unknown): "0" | "1" => (isChecked(v) ? "1" : "0");

/**
 * The amount as the IRS writes it: two decimals, sign kept. "$1,234.50" and 1234.5 are the
 * same amount; the shared parseAmount reads "$1,234.50" as 0, so the symbols go first.
 */
export function amountOf(value: unknown): string {
  const parsed = parseFloat(text(value).replace(/[$,\s]/g, ""));
  return (Number.isFinite(parsed) ? parsed : 0).toFixed(2);
}

const hasValue = (v: unknown): boolean => text(v) !== "";

const amountIfPresent = (element: string, value: unknown) =>
  hasValue(value) ? { [element]: amountOf(value) } : {};

export const isForeignRecipient = (record: Record1099Q): boolean =>
  isChecked(record.FOREIGN_COUNTRY_INDICATOR) || text(record.FOREIGN_COUNTRY) !== "";

export function recipientTin(record: Record1099Q): string {
  return text(record.RECIPIENT_ID_NUMBER).replace(/\D/g, "") || "000000000";
}

/**
 * The TIN the IRS will see for this recipient. A test transmission masks it, and the masked
 * value is what the IRS holds for the original, so a correction's PrevSubmittedRecRecipientGrp
 * must carry the same masked TIN (core-api found this the hard way, PR #1768).
 */
export function filedRecipientTin(record: Record1099Q, testFile: boolean): string {
  return testFile ? "000000000" : recipientTin(record);
}

/** Recipient mailing address: USAddress, or ForeignAddress when the row is foreign. */
export function recipientMailingAddress(record: Record1099Q, schemaRoot?: string): object {
  const { line1, line2 } = streetLinesWithUnit(
    record.RECIPIENT_ADDRESS1,
    record.RECIPIENT_ADDRESS2,
    record.RECIPIENT_APT_NO,
  );
  if (isForeignRecipient(record)) {
    const country = text(record.FOREIGN_COUNTRY);
    return {
      ForeignAddress: {
        AddressLine1Txt: line1 || "NA",
        ...(line2 && { AddressLine2Txt: line2 }),
        ...(text(record.RECIPIENT_CITY) && { CityNm: text(record.RECIPIENT_CITY) }),
        ...(text(record.RECIPIENT_STATE) && { ProvinceOrStateNm: text(record.RECIPIENT_STATE) }),
        // An unresolvable name goes through raw so the XSD names CountryCd.
        CountryCd:
          countryToIso(country, schemaRoot) ??
          country.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase(),
        ...(text(record.RECIPIENT_ZIP) && { ForeignPostalCd: text(record.RECIPIENT_ZIP) }),
      },
    };
  }
  return {
    USAddress: {
      AddressLine1Txt: line1 || "UNKNOWN ADDRESS",
      ...(line2 && { AddressLine2Txt: line2 }),
      CityNm: text(record.RECIPIENT_CITY) || "UNKNOWN",
      StateAbbreviationCd: text(record.RECIPIENT_STATE).toUpperCase().slice(0, 2),
      ZIPCd: text(record.RECIPIENT_ZIP) || "00000",
    },
  };
}

/** RecipientDetail (person or business, US or foreign). */
export function recipientDetail1099Q(
  record: Record1099Q,
  testFile: boolean,
  schemaRoot?: string,
): object {
  const tin = filedRecipientTin(record, testFile);
  const mailing = recipientMailingAddress(record, schemaRoot);
  return (
    buildSplitRecipientDetail(record, tin, mailing) ?? {
      // A row with no name at all: record checks refuse these, so this only guards a record
      // edited around them.
      TIN: tin,
      TINSubmittedTypeCd: "INDIVIDUAL_TIN",
      PersonNameControlTxt: "UNKN",
      PersonName: { PersonFirstNm: "UNKNOWN", PersonLastNm: "UNKNOWN" },
      MailingAddressGrp: mailing,
    }
  );
}

/** Form1099QDistributionCd takes 1 to 6. Anything else is left out rather than filed. */
export const DISTRIBUTION_CODES = ["1", "2", "3", "4", "5", "6"] as const;

export function distributionCode(record: Record1099Q): string | null {
  const code = text(record.DISTRIBUTION_CODE);
  return (DISTRIBUTION_CODES as readonly string[]).includes(code) ? code : null;
}

/**
 * Everything after RecipientDetail, in schema order. The six indicators are required by the
 * XSD (no minOccurs="0"), so each is always written, as 0 when the box is blank.
 */
export function form1099QBoxes(record: Record1099Q): Record<string, any> {
  const account = text(record.ACCOUNT_NUMBER);
  const code = distributionCode(record);
  return {
    ...(account && { RecipientAccountNum: account.slice(0, 30) }),
    ...amountIfPresent("GrossDistributionAmt", record.BOX1_GROSS_DISTRIBUTION),
    ...amountIfPresent("EarningsAmt", record.BOX2_EARNINGS),
    ...amountIfPresent("BasisAmt", record.BOX3_BASIS),
    TrusteeToTrusteeTransferInd: digit(record.BOX4A_TRUSTEE_TO_TRUSTEE_TRANSFER),
    QTPToRothIRATransferInd: digit(record.BOX4B_QTP_TO_ROTH_IRA_TRANSFER),
    DistriFromPrivateQTPInd: digit(record.BOX5_PRIVATE_QTP),
    DistriFromStateQTPInd: digit(record.BOX5_STATE_QTP),
    DistriFromCoverdellESAInd: digit(record.BOX5_COVERDELL_ESA),
    NotDesignatedBeneficiaryInd: digit(record.BOX6_NOT_DESIGNATED_BENEFICIARY),
    ...(code && { Form1099QDistributionCd: code }),
  };
}

/** Issuer-level totals: the three amount boxes Form1099QTotalAmtGrpType carries. */
export function form1099QTotals(records: Record1099Q[]): Record<string, string> {
  const sum = (column: keyof Record1099Q) =>
    records.reduce((total, r) => total + parseFloat(amountOf(r[column])), 0).toFixed(2);
  return {
    GrossDistributionAmt: sum("BOX1_GROSS_DISTRIBUTION"),
    EarningsAmt: sum("BOX2_EARNINGS"),
    BasisAmt: sum("BOX3_BASIS"),
  };
}
